import { Theme, useTheme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { createSxStyles } from "@common/createSxStyles";

export type PaneSlot = "right" | "stage";
export type FrameSlot = PaneSlot | "fullscreen" | "closed";

// Every occupant is a floating pane: this margin on all four sides, a radius and a shadow, so the seam
// between the stage and the rail reads as a gap. One number drives every measurement below.
export const railMargin = 8;

// Set on the handle itself for as long as a drag lasts. The handle has no descendants, so toggling it
// restyles one element; keeping the flag in React cost a full-page render on every press and release.
export const railHandleDraggingAttribute = "data-dragging";

// Marks every element whose geometry follows the rail width, with the slot it currently occupies as the
// value. The drag finds them through this rather than through refs, because the panes that have to move
// together live in different components.
export const frameSlotAttribute = "data-frame-slot";

// Above the drawer and the navigation popper (both 1200), below MUI's modals (1300) so dialogs and
// tooltips opened from inside a pane land on top of it. The rail sits above the stage so its handle
// wins where the two meet.
const railZIndex: Record<PaneSlot | "fullscreen", number> = { stage: 1210, right: 1220, fullscreen: 1250 };

// The handle shares the pane's stacking context with everything the occupant renders, so it has to
// outrank the occupant's own overlays rather than merely sit above the page beneath it. The document
// viewer's spinner and its "failed to load" state are absolutely positioned at z-index 1 across the whole
// viewer, and at an equal z-index the later element wins: the handle was unhoverable below the toolbar
// for any document that was still loading or had failed.
const railHandleZIndex = 100;

export function railTransition(theme: Theme, properties: Array<string>): string {
    return theme.transitions.create(properties, {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    });
}

// One formatter per edge the drag writes, shared by the style prop and the drag so the string React
// commits on release is byte-identical to the one the last pointer move left behind and nothing jumps.
export function formatRightPaneLeft(railWidth: number): string {
    return `calc(100% - ${railWidth + railMargin}px)`;
}

// The two panes float side by side with the same margin between them that each keeps from the page edge,
// so both draw their own full border and radius instead of meeting in a doubled seam.
export function formatStagePaneRight(railWidth: number, isRailOccupied: boolean): string {
    return `${isRailOccupied ? railWidth + railMargin * 2 : railMargin}px`;
}

export function formatSpacerWidth(railWidth: number, isRailOccupied: boolean): string {
    return `${isRailOccupied ? railWidth + railMargin : 0}px`;
}

export interface FrameEdges {
    top: number;
    bottom: number;
    left: string;
    right: string;
}

/**
 * A pane is sized by its four edges alone, never by a width or a viewport unit, so the Windows scrollbar
 * and mobile viewport quirks never enter into it. Below the md breakpoint there is no rail: every open
 * pane takes the whole viewport.
 */
export function getFrameEdges(slot: PaneSlot | "fullscreen", railWidth: number, isRailOccupied: boolean, isSmallScreen: boolean): FrameEdges {
    if (slot === "fullscreen" || isSmallScreen) {
        return { top: 0, bottom: 0, left: "0px", right: "0px" };
    }

    if (slot === "right") {
        return { top: railMargin, bottom: railMargin, left: formatRightPaneLeft(railWidth), right: `${railMargin}px` };
    }

    return { top: railMargin, bottom: railMargin, left: `${railMargin}px`, right: formatStagePaneRight(railWidth, isRailOccupied) };
}

export interface FrameStyleOptions {
    slot: FrameSlot;
    defaultSlot: PaneSlot;
    isSmallScreen: boolean;
    /**
     * An occupant whose edge a plain border cannot paint. The chat's is a brand gradient, which has to be
     * a clipped background image; left out, the pane draws the ordinary single-colour edge.
     */
    borderTreatment?: SystemStyleObject<Theme>;
    resizeHandleColor: string;
    expandedDirection: "row" | "column";
    /**
     * Set on the commit where a closed pane opens. A frame keeps the slot it last showed in while it is
     * parked, so the first pane of a session opens in a slot the frame was not parked in and its edges
     * change in the same commit as the slide. Animating that would sweep the pane across the width of the
     * slot it is leaving before it settles, so on this one commit the edges are taken up without animating
     * and only the slide in is seen.
     */
    isOpening: boolean;
}

export const useFrameStyles = (options: FrameStyleOptions) => {
    const theme = useTheme();
    const isClosed = options.slot === "closed";
    const isFullscreen = options.slot === "fullscreen";
    const geometrySlot: PaneSlot | "fullscreen" = options.slot === "closed" ? options.defaultSlot : options.slot;
    const hasPaneChrome = !isFullscreen && !options.isSmallScreen;
    const slide = railTransition(theme, options.isOpening ? ["transform"] : ["transform", "left", "right"]);

    // Every pane parks past its own right edge, margin included, so its shadow is off-screen too. A closed
    // pane already carries its open edges, so a stage pane parks with its left edge exactly on the right
    // pane's, hidden behind it, and opens by sweeping left out from under the rail rather than in from the
    // far side of the screen. Sliding the transform rather than the width keeps the pane's full width for
    // every frame, so nothing inside reflows while it moves.
    const closedTransform = `translateX(calc(100% + ${railMargin}px))`;

    // No edges here: PaneFrame writes them through the style prop, the same inline properties the drag
    // writes to between commits, so the class never has to be re-minted per width.
    return createSxStyles({
        pane: {
            position: "fixed",
            zIndex: railZIndex[geometrySlot],
            display: "flex",
            flexDirection: isFullscreen ? options.expandedDirection : "column",
            backgroundColor: theme.customPalette.white,
            overflow: "hidden",
            borderRadius: 2,
            boxShadow: hasPaneChrome ? "0 16px 40px rgba(0, 0, 0, 0.12)" : "none",
            ...(isFullscreen ? {} : (options.borderTreatment ?? { border: `2px solid ${theme.palette.primary.main}` })),
            transform: isClosed ? closedTransform : "none",
            // Hidden only once the slide out has finished, and visible again the moment a slide in starts.
            visibility: isClosed ? "hidden" : "visible",
            pointerEvents: isClosed ? "none" : "auto",
            transition: isClosed ? `${slide}, visibility 0s linear ${theme.transitions.duration.enteringScreen}ms` : slide,
        },
        resizeHandle: {
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 6,
            cursor: "col-resize",
            zIndex: railHandleZIndex,
            // Without this the browser claims the gesture for panning and no pointermove ever arrives.
            touchAction: "none",
            backgroundColor: "transparent",
            [`&:hover, &[${railHandleDraggingAttribute}]`]: {
                backgroundColor: options.resizeHandleColor,
            },
        },
    });
};
